import { type Brand, fetchGroupName } from '@utils/helpers/getGroup'
import { useRouter } from 'next/router'
import type { ParsedUrlQuery } from 'querystring'
import type { ReactElement } from 'react'
import { createContext, useContext, useEffect, useState } from 'react'

import { useBrand } from './useBrand'
import { usePostMessage } from './usePostMessage'

type Props = {
  children: ReactElement
}

type QueryContextType = {
  query: ParsedUrlQuery
  setQuery: (
    query: ParsedUrlQuery | ((prev: ParsedUrlQuery) => ParsedUrlQuery),
  ) => void
}

const QueryContext = createContext<QueryContextType | undefined>(undefined)

const QueryProvider = ({ children }: Props) => {
  const router = useRouter()
  const brand = useBrand()
  const [enhancedQuery, setEnhancedQuery] = useState(router.query)
  const [postMessageProcessed, setPostMessageProcessed] = useState(false)

  const { data: messageData, received } = usePostMessage(
    'gb_form_getPreferredDealer',
    'gb_aem_getPreferredDealer',
  )

  useEffect(() => {
    if (received && messageData?.dealer) {
      if (messageData.retailerType === 'retailer') {
        setEnhancedQuery((prevQuery) => ({
          ...prevQuery,
          group: messageData.dealer,
          groupName: messageData.dealerName || '',
        }))
      }
      setPostMessageProcessed(true)
    } else if (received) {
      setPostMessageProcessed(true)
    }
  }, [received, messageData])

  useEffect(() => {
    const fetchAndEnhanceQuery = async () => {
      const query = { ...router.query }

      if (enhancedQuery.group && postMessageProcessed) {
        setEnhancedQuery((prev) => ({
          ...query,
          group: prev.group,
          groupName: prev.groupName,
        }))
        return
      }

      // Fetch group name from API if group is in URL but no groupName yet
      if (query.group && !query.groupName && typeof window !== 'undefined') {
        const groupName = await fetchGroupName(
          query.group as string,
          brand as Brand,
        )
        query.groupName = groupName
      }

      setEnhancedQuery(query)
    }

    fetchAndEnhanceQuery()
  }, [router.query, brand, postMessageProcessed])

  return (
    <QueryContext.Provider
      value={{ query: enhancedQuery, setQuery: setEnhancedQuery }}
    >
      {children}
    </QueryContext.Provider>
  )
}

const useQuery = () => {
  const context = useContext(QueryContext)
  if (!context) {
    throw new Error('useQuery must be used within QueryProvider')
  }
  return context.query
}

const useQueryContext = () => {
  const context = useContext(QueryContext)
  if (!context) {
    throw new Error('useQueryContext must be used within QueryProvider')
  }
  return context
}

export { QueryProvider, useQuery, useQueryContext }
